import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

export interface KBSettings {
  // Parser Settings (requires reparse)
  chunk_method: string
  chunk_token_count: number
  language: string
  delimiter: string
  auto_keywords: number
  auto_questions: number
  
  // Retrieval/Chat Settings (instant effect)
  similarity_threshold: number
  keywords_similarity_weight: number
  top_n: number
  show_quote: boolean
  prompt: string
  rerank_model: string

  // Agent Settings
  agent: boolean
  agent_id: string

  // Crawler Settings
  enable_crawler: boolean
  crawler_include_domains: string
  crawler_exclude_domains: string
  crawler_max_depth?: number
  crawler_max_pages?: number
  
  // Privacy Settings
  permission: string
}

export interface Template {
  name: string
  description: string
  settings: Partial<KBSettings>
}

// Fetch current settings
export function useSettings(kbId: string) {
  return useQuery({
    queryKey: ['kb-settings', kbId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/kb/${kbId}/settings/`)
      return response.data as KBSettings
    },
    enabled: !!kbId,
  })
}

// Fetch available templates
export function useTemplates() {
  return useQuery({
    queryKey: ['kb-templates'],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/templates/')
      return response.data as Record<string, Template>
    },
  })
}

// Update settings
export function useUpdateSettings(kbId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (settings: Partial<KBSettings>) => {
      const response = await knowledgeApi.post(
        `/knowledge/api/kb/${kbId}/settings/`,
        settings
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-settings', kbId] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
    },
  })
}

// Apply template
export function useApplyTemplate(kbId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (templateKey: string) => {
      const response = await knowledgeApi.post(
        `/knowledge/api/kb/${kbId}/settings/apply-template/`,
        { template: templateKey }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-settings', kbId] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
    },
  })
}

// Delete knowledge base
export function useDeleteKB(kbId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await knowledgeApi.post(
        `/knowledge/${kbId}/delete/`
      )
      return response.data
    },
  })
}
